import { useState } from "react";
import { Button } from "../ui/button";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { MapPin, MagnifyingGlass, Warning, PencilSimple } from "@phosphor-icons/react";

/**
 * AuditImprecisePinsCard — admin tool that scans every schedule doc,
 * re-geocodes each entry's typed address, and surfaces the ones whose
 * stored pin is either:
 *   - SUSPICIOUS: stored coord is >10mi from where the address actually
 *     resolves (rare; usually a state-mismatch geocode that got past
 *     the service-area guard).
 *   - IMPRECISE: stored coord matches the geocode but the geocode only
 *     hit a city/town/ZIP centroid (the most common cause of the
 *     "directions take me to the right town but wrong block" bug).
 *
 * Each flagged row is one entry, sorted with worst-first so the admin
 * can knock them out top-down. The fix UI uses two paths: copy the
 * curl command for one-off edits, or click "Open editor" to jump to
 * that owner's dashboard (future iteration — for now the curl is
 * documented inline).
 */
export default function AuditImprecisePinsCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState("all"); // "all" | "suspicious" | "imprecise"

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/audit-imprecise-pins");
      setResult(data);
      const flaggedTotal = data.suspicious_count + data.imprecise_count;
      if (flaggedTotal === 0) {
        toast.success(`Audit clean — ${data.ok_count} entries, no issues found.`);
      } else {
        toast.warning(
          `${flaggedTotal} entries flagged · ${data.suspicious_count} suspicious + ${data.imprecise_count} imprecise.`
        );
      }
    } catch (e) {
      toast.error(formatApiError(e, "Audit failed"));
    } finally {
      setBusy(false);
    }
  };

  const flagged = result?.flagged || [];
  const filtered = flagged.filter(
    (f) => filter === "all" || f.pin_check.status === filter
  );

  return (
    <section
      className="mb-6 rounded-2xl border border-border bg-card p-5"
      data-testid="audit-imprecise-pins-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <MapPin size={18} weight="duotone" className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight">
            Audit imprecise pins
          </h2>
        </div>
        <Button
          size="sm"
          onClick={run}
          disabled={busy}
          data-testid="audit-imprecise-pins-run"
          className="gap-2"
        >
          <MagnifyingGlass size={14} weight="bold" />
          {busy ? "Scanning…" : result ? "Re-scan" : "Run audit"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
        Re-geocodes every schedule entry and flags pins that resolved to
        a city/town centroid (<strong>imprecise</strong>) or sit &gt;10 miles
        from the typed address (<strong>suspicious</strong>). Uses the
        30-day geocode cache — re-runs are basically free.
      </p>

      {result && (
        <div className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            <SummaryStat label="Schedules scanned" value={result.scanned_schedules} />
            <SummaryStat label="Entries scanned" value={result.scanned_entries} />
            <SummaryStat
              label="Suspicious"
              value={result.suspicious_count}
              tone={result.suspicious_count > 0 ? "danger" : "ok"}
            />
            <SummaryStat
              label="Imprecise"
              value={result.imprecise_count}
              tone={result.imprecise_count > 0 ? "warn" : "ok"}
            />
          </div>

          {flagged.length > 0 && (
            <>
              <div className="mt-4 inline-flex bg-muted rounded-full p-0.5">
                {[
                  { v: "all", l: `All (${flagged.length})` },
                  { v: "suspicious", l: `Suspicious (${result.suspicious_count})` },
                  { v: "imprecise", l: `Imprecise (${result.imprecise_count})` },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setFilter(opt.v)}
                    className={`h-7 px-3 rounded-full text-xs font-semibold transition ${
                      filter === opt.v
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`audit-filter-${opt.v}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>

              <div className="mt-3 divide-y divide-border rounded-xl border border-border overflow-hidden">
                {filtered.map((f, i) => (
                  <PinRow key={`${f.schedule_id}-${f.day}-${f.index}-${i}`} f={f} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function SummaryStat({ label, value, tone = "neutral" }) {
  const toneClass = {
    neutral: "",
    ok: "text-emerald-700 dark:text-emerald-400",
    warn: "text-amber-700 dark:text-amber-400",
    danger: "text-red-700 dark:text-red-400",
  }[tone];
  return (
    <div className="rounded-lg border border-border bg-background/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`font-display font-bold text-xl tabular-nums leading-tight ${toneClass}`}>
        {value}
      </div>
    </div>
  );
}

function PinRow({ f }) {
  const [showFix, setShowFix] = useState(false);
  const status = f.pin_check.status;
  const badgeClass =
    status === "suspicious"
      ? "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300"
      : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300";
  const fixCurl = `curl -s -X POST "$PROD/api/admin/schedule-fix-pin" \\
  -H "Authorization: Bearer $TOKEN" -H "Content-Type: application/json" \\
  -d '${JSON.stringify({
    schedule_id: f.schedule_id,
    day: f.day,
    index: f.index,
    lat: f.pin_check.geocoded?.lat ?? f.lat,
    lng: f.pin_check.geocoded?.lng ?? f.lng,
  })}'`;
  return (
    <div className="px-3 py-3" data-testid={`audit-row-${f.schedule_id}-${f.day}-${f.index}`}>
      <div className="flex items-start gap-3">
        <span
          className={`flex-none inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${badgeClass}`}
        >
          <Warning size={10} weight="bold" /> {status}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">
            {f.truck_name || "(no name)"} <span className="text-muted-foreground font-normal">· {f.day} #{f.index + 1}</span>
          </div>
          <div className="text-xs text-muted-foreground truncate" title={f.owner_email}>
            {f.owner_email}
          </div>
          <div className="mt-1 text-xs">
            <span className="text-muted-foreground">typed: </span>
            <span className="font-mono">{f.address}</span>
          </div>
          <div className="text-xs">
            <span className="text-muted-foreground">geocoded: </span>
            <span className="font-mono">{f.pin_check.geocoded?.display || "—"}</span>
          </div>
          <div className="text-xs">
            <span className="text-muted-foreground">drift: </span>
            <span className="tabular-nums">
              {f.pin_check.miles ?? 0} mi
            </span>
            <span className="text-muted-foreground"> · {f.pin_check.precision_label}</span>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="flex-none gap-1.5"
          onClick={() => setShowFix((v) => !v)}
          data-testid={`audit-row-fix-toggle-${f.schedule_id}-${f.day}-${f.index}`}
        >
          <PencilSimple size={12} weight="bold" />
          Fix
        </Button>
      </div>
      {showFix && (
        <div className="mt-3 ml-9 rounded-lg bg-muted/60 border border-border p-3 text-[11px] font-mono whitespace-pre-wrap break-all">
          {fixCurl}
        </div>
      )}
    </div>
  );
}
